import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  CHIPS_ALLOWED_MODULE,
  type IFilterChip,
  type IFilterItem,
  type IFiltersInitialValues,
  type ISearchParameters,
  OPERATOR_SORT,
} from '@features/filters/types';
import { getChipBasedFiltersInitialValues } from '@features/filters/utils/getChipBasedFiltersInitialValues';
import { hasPartialUpdates } from '@utils/hasPartialUpdates';

interface IInitialState {
  chipList: IFilterChip[];
  gridQueryParams: string;
  filterList: IFilterItem[];
  prevFilters: IFiltersInitialValues;
  activePlayerBonusId: null | string;
  searchParameters: ISearchParameters;
  isReturnedFromDetailedView: boolean;
}

export const initialPlayerBonusesSearchParameters = {
  page: 1,
  size: 10,
  filters: [],
  isIncludeExternalBonuses: true,
  sorts: [
    {
      field: 'given_at',
      order: OPERATOR_SORT.DESC,
    },
  ],
};

const initialState: IInitialState = {
  chipList: [],
  filterList: [],
  gridQueryParams: '',
  activePlayerBonusId: null,
  isReturnedFromDetailedView: false,
  searchParameters: initialPlayerBonusesSearchParameters,
  prevFilters: getChipBasedFiltersInitialValues(CHIPS_ALLOWED_MODULE.BONUSES),
};

const playerBonusesSlice = createSlice({
  initialState,
  name: 'playerBonusesSlice',
  reducers: {
    resetChipList(state: IInitialState) {
      state.chipList = initialState.chipList;
    },
    setChipList(state: IInitialState, action: PayloadAction<IFilterChip[]>) {
      state.chipList = action.payload;
    },
    setFilterList(state: IInitialState, action: PayloadAction<IFilterItem[]>) {
      state.filterList = action.payload;
    },
    addChipItem(state: IInitialState, action: PayloadAction<IFilterChip>) {
      state.chipList = [...state.chipList, action.payload];
    },
    addFilter(state: IInitialState, action: PayloadAction<IFilterItem>) {
      state.filterList = [...state.filterList, action.payload];
    },
    setPrevFilters(
      state: IInitialState,
      action: PayloadAction<IInitialState['prevFilters']>
    ) {
      state.prevFilters = action.payload;
    },
    setGridQueryParams(
      state: IInitialState,
      action: PayloadAction<IInitialState['gridQueryParams']>
    ) {
      state.gridQueryParams = action.payload;
    },
    removeChipItem(state: IInitialState, action: PayloadAction<string>) {
      state.chipList = state.chipList.filter(
        (item) => item.field !== action.payload
      );
    },
    removeFilter(state: IInitialState, action: PayloadAction<string>) {
      state.filterList = state.filterList.filter(
        (item) => item.value !== action.payload
      );
    },
    setActivePlayerBonusId(
      state: IInitialState,
      action: PayloadAction<IInitialState['activePlayerBonusId']>
    ) {
      state.activePlayerBonusId = action.payload;
    },
    setIsReturnedFromDetailedView(
      state: IInitialState,
      action: PayloadAction<IInitialState['isReturnedFromDetailedView']>
    ) {
      state.isReturnedFromDetailedView = action.payload;
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      if (!hasPartialUpdates(state.searchParameters, action.payload)) {
        return;
      }

      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
  },
});

export default playerBonusesSlice.reducer;

export const {
  addFilter,
  addChipItem,
  setChipList,
  removeFilter,
  resetChipList,
  setFilterList,
  removeChipItem,
  setPrevFilters,
  setGridQueryParams,
  setSearchParameters,
  setActivePlayerBonusId,
  setIsReturnedFromDetailedView,
} = playerBonusesSlice.actions;
